import { AGENT_STATUS, type StatusMeta } from "@/shared/ui/status";
import type { DashPhone } from "@/shared/ws/wsTypes";
import { parseAppDateTime } from "./dates";

export const PHONE_STATUSES = ["OFFLINE", "PAUSED", "ONLINE", "INUSE", "RINGING", "DIALING"] as const;
export type PhoneStatusKey = (typeof PHONE_STATUSES)[number];

/** Rótulos pt-BR (mesmos do design system: `AGENT_STATUS`). */
export const STATUS_LABEL: Record<PhoneStatusKey, string> = {
  OFFLINE: AGENT_STATUS.OFFLINE.label,
  PAUSED: AGENT_STATUS.PAUSED.label,
  ONLINE: AGENT_STATUS.ONLINE.label,
  INUSE: AGENT_STATUS.INUSE.label,
  RINGING: AGENT_STATUS.RINGING.label,
  DIALING: AGENT_STATUS.DIALING.label,
};

export function isPhoneStatusKey(s: unknown): s is PhoneStatusKey {
  return typeof s === "string" && (PHONE_STATUSES as readonly string[]).includes(s);
}

/** Meta (cor + ícone + rótulo) de um status cru da sessão/telefone; desconhecido → texto cru, tom offline. */
export function statusMetaOf(raw: unknown): StatusMeta {
  if (isPhoneStatusKey(raw)) return AGENT_STATUS[raw];
  return { ...AGENT_STATUS.OFFLINE, label: raw == null || raw === "" ? AGENT_STATUS.OFFLINE.label : String(raw) };
}

/**
 * Status visual (classe do card/linha) — gui `rowClass`/`PhoneSummary`:
 * sessão PAUSED → PAUSED; sessão OFFLINE → OFFLINE; senão o status do telefone.
 */
export function rowStatus(p: DashPhone): PhoneStatusKey {
  if (p.sessionStatus === "PAUSED") return "PAUSED";
  if (p.sessionStatus === "OFFLINE") return "OFFLINE";
  switch (p.phoneStatus) {
    case "ONLINE":
    case "RINGING":
    case "INUSE":
    case "DIALING":
      return p.phoneStatus;
    default:
      return "OFFLINE";
  }
}

/**
 * Status usado no filtro por chip (gui index.vue `validateStatus`):
 * sessão PAUSED/OFFLINE prevalece; senão o status do telefone.
 */
export function filterStatus(p: DashPhone): string {
  if (p.sessionStatus === "PAUSED" || p.sessionStatus === "OFFLINE") return p.sessionStatus;
  return String(p.phoneStatus ?? "OFFLINE");
}

/** Contagem dos chips (gui PhoneStatusSum.sum). */
export function countStatuses(phones: DashPhone[]): Record<PhoneStatusKey, number> {
  const total: Record<PhoneStatusKey, number> = {
    OFFLINE: 0,
    PAUSED: 0,
    ONLINE: 0,
    INUSE: 0,
    RINGING: 0,
    DIALING: 0,
  };
  for (const p of phones) {
    if (p.sessionStatus === "OFFLINE") {
      total.OFFLINE++;
      continue;
    }
    switch (p.phoneStatus) {
      case "ONLINE":
        if (p.sessionStatus === "PAUSED") total.PAUSED++;
        else total.ONLINE++;
        break;
      case "RINGING":
        total.RINGING++;
        break;
      case "INUSE":
        total.INUSE++;
        break;
      case "DIALING":
        total.DIALING++;
        break;
      default:
        total.OFFLINE++;
    }
  }
  return total;
}

/** Texto do card: na pausa mostra o status de sessão, senão o do telefone. */
export function headlineStatus(p: DashPhone): string {
  return p.sessionStatus !== "PAUSED" ? String(p.phoneStatus ?? "OFFLINE") : "PAUSED";
}

/** Info curta: nome da pausa (sessionInfo) quando pausado, senão phoneInfo. */
export function shortInfo(p: DashPhone): string {
  return String((p.sessionStatus !== "PAUSED" ? p.phoneInfo : p.sessionInfo) ?? "");
}

/** Texto cru do início do status (gui apiWs: maior entre sessão e telefone; fallback phoneStatusBegin). */
export function statusBeginRaw(p: DashPhone): string | undefined {
  return p.statusBegin || p.phoneStatusBegin || undefined;
}

/**
 * Início do status como instante. `sessionStatusBegin`/`phoneStatusBegin` chegam como relógio de
 * parede no fuso da APLICAÇÃO (não UTC) — ver `parseAppDateTime`/`useAppTimezone`.
 */
export function statusBeginAt(p: DashPhone, appTimeZone: string): Date | null {
  return parseAppDateTime(statusBeginRaw(p), appTimeZone);
}

/** Início do cronômetro (gui PhoneSummary): nulo quando offline. */
export function timerBegin(p: DashPhone, appTimeZone: string): Date | null {
  if (p.sessionStatus === "OFFLINE") return null;
  switch (p.phoneStatus) {
    case "ONLINE":
    case "RINGING":
    case "INUSE":
    case "DIALING":
      return statusBeginAt(p, appTimeZone);
    default:
      return null;
  }
}

/** Tempo limite (min) da pausa atual, a partir de `phone.breaks.data` (title == sessionInfo). */
export function breakTimeLimit(p: DashPhone): number {
  if (!p.breaks?.data?.length || !p.sessionInfo) return 0;
  const b = p.breaks.data.find((x) => x.title === p.sessionInfo);
  const lim = Number(b?.timeLimit ?? 0);
  return Number.isFinite(lim) && lim > 0 ? lim : 0;
}

/** Pausas com cronômetro regressivo na gui (DigitalClock). */
export const COUNTDOWN_BREAKS = new Set(["Almoço", "NR1", "NR2", "Almoço SAC"]);

export function displayName(p: DashPhone): string {
  const full = String(p.fullName ?? "").trim();
  if (full.length > 1) return full;
  return String(p.username ?? "");
}

export function sortByName(a: DashPhone, b: DashPhone): number {
  const na = String(a.fullName ?? a.username ?? "");
  const nb = String(b.fullName ?? b.username ?? "");
  return na.localeCompare(nb);
}

export function queueChips(p: DashPhone) {
  return (p.queues ?? []).map((q) => ({ key: String(q.queueNumber), label: String(q.displayName ?? q.queueNumber) }));
}

export function tagChips(p: DashPhone) {
  return (p.tags ?? []).map((t) => ({ key: String(t.name), label: String(t.displayName ?? t.name) }));
}
